<app-page-breadcrumb [pageTitle]="pageTitle"></app-page-breadcrumb>

<div class="grid grid-cols-1 gap-6 max-w-2xl">
  <app-component-card [title]="pageTitle" desc="Define el esquema de un campo personalizado para los contactos">

    <!-- Loading -->
    @if (loading) {
    <div class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
      <span class="ml-3 text-gray-500 dark:text-gray-400">Cargando datos...</span>
    </div>
    }

    <!-- Error -->
    @if (error) {
    <div class="mb-5 p-4 bg-error-50 border border-error-200 rounded-xl dark:bg-error-500/10 dark:border-error-500/20 flex items-start gap-3">
      <svg class="w-5 h-5 text-error-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>
      <p class="text-sm text-error-600 dark:text-error-400">{{ error }}</p>
    </div>
    }

    @if (!loading) {
    <form (ngSubmit)="guardar()" #defForm="ngForm" class="space-y-7">

      <!-- ── Nombre ── -->
      <div>
        <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Nombre del campo <span class="text-error-500">*</span>
        </label>
        <div class="relative">
          <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
            <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
            </svg>
          </div>
          <input
            type="text"
            name="name"
            [(ngModel)]="name"
            #nameField="ngModel"
            required
            maxlength="30"
            pattern="^[A-Za-zÀ-ÿÑñ\s'-]*$"
            placeholder="Ej: Industria, Nivel de interés, Empresa principal…"
            class="dark:bg-dark-900 h-11 w-full rounded-xl border border-gray-300 bg-transparent pl-10 pr-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
          />
        </div>
        @if (nameField.invalid && (nameField.dirty || nameField.touched)) {
          @if (nameField.errors?.['pattern']) {
            <p class="mt-1.5 text-xs text-error-500">Solo se permiten letras, espacios, apóstrofes y guiones (sin números ni símbolos).</p>
          } @else if (nameField.errors?.['maxlength']) {
            <p class="mt-1.5 text-xs text-error-500">La cantidad máxima de caracteres permitida es de 30.</p>
          }
        }
        @if (name.trim()) {
        <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
          Vista previa:
          <span class="ml-1 font-medium text-gray-700 dark:text-gray-200">{{ name.trim() }}</span>
          @if (type === 'boolean') { <span class="ml-1 text-gray-400">— Sí / No</span> }
          @if (type === 'number')  { <span class="ml-1 text-gray-400">— Número</span> }
          @if (type === 'list' && listOptionsList.length > 0) { <span class="ml-1 text-gray-400">— {{ listOptionsList[0] }}{{ listOptionsList.length > 1 ? ', …' : '' }}</span> }
        </p>
        } @else {
        <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">Etiqueta visible para el usuario en los formularios de contacto</p>
        }
      </div>

      <!-- ── Tipo de dato ── -->
      <div>
        <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Tipo de dato <span class="text-error-500">*</span>
        </label>
        <div class="grid grid-cols-2 gap-3">
          @for (opt of typeOptions; track opt.value) {
          <button
            type="button"
            (click)="selectType(opt.value)"
            class="flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition-all focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            [class]="type === opt.value
              ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10'
              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-900'">

            <!-- Icono -->
            <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
              [class]="type === opt.value
                ? 'bg-brand-500 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'">
              @if (opt.value === 'text') {
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M4 6h16M4 12h10M4 18h7"></path>
                </svg>
              }
              @if (opt.value === 'number') {
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"></path>
                </svg>
              }
              @if (opt.value === 'boolean') {
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              }
              @if (opt.value === 'list') {
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"></path>
                </svg>
              }
            </div>

            <!-- Texto -->
            <div class="min-w-0">
              <p class="text-sm font-semibold leading-tight"
                [class]="type === opt.value ? 'text-brand-600 dark:text-brand-400' : 'text-gray-800 dark:text-gray-200'">
                {{ opt.label }}
              </p>
              <p class="text-xs leading-tight mt-0.5 text-gray-500 dark:text-gray-400">{{ opt.description }}</p>
            </div>
          </button>
          }
        </div>
      </div>

      <!-- ── Opciones de lista ── -->
      @if (type === 'list') {
      <div>
        <div class="mb-2 flex items-center justify-between">
          <label class="text-sm font-medium text-gray-700 dark:text-gray-300">
            Opciones de lista <span class="text-error-500">*</span>
          </label>
          @if (listOptionsList.length > 0) {
          <span class="text-xs font-medium px-2 py-0.5 rounded-full bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400">
            {{ listOptionsList.length }} {{ listOptionsList.length === 1 ? 'opción' : 'opciones' }}
          </span>
          }
        </div>

        <!-- Chips de opciones existentes -->
        @if (listOptionsList.length > 0) {
        <div class="mb-3 flex flex-wrap gap-2 p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 min-h-12">
          @for (opt of listOptionsList; track opt; let i = $index) {
          <span class="inline-flex items-center gap-1 pl-3 pr-1 py-1 rounded-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-200 shadow-sm">
            <span class="text-xs text-gray-400 dark:text-gray-500 font-mono mr-0.5">{{ i + 1 }}.</span>
            {{ opt }}
            <span class="flex items-center gap-0.5 ml-1">
              <button type="button" (click)="moveOption(i, -1)" [disabled]="i === 0"
                class="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Subir">
                <svg class="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 15l7-7 7 7"></path>
                </svg>
              </button>
              <button type="button" (click)="moveOption(i, 1)" [disabled]="i === listOptionsList.length - 1"
                class="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Bajar">
                <svg class="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
              <button type="button" (click)="removeOption(i)"
                class="p-0.5 rounded hover:bg-error-100 dark:hover:bg-error-900/30 transition-colors"
                title="Eliminar opción">
                <svg class="w-3 h-3 text-gray-400 hover:text-error-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </span>
          </span>
          }
        </div>
        } @else {
        <div class="mb-3 flex items-center justify-center py-5 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600">
          <p class="text-sm">Agrega la primera opción abajo</p>
        </div>
        }

        <!-- Input para agregar nueva opción -->
        <div class="flex gap-2">
          <input
            type="text"
            [(ngModel)]="newOptionInput"
            name="newOptionInput"
            [ngModelOptions]="{standalone: true}"
            (keydown.enter)="addOption(); $event.preventDefault()"
            placeholder="Escribe una opción y presiona Enter o Agregar…"
            class="dark:bg-dark-900 h-10 flex-1 rounded-lg border border-gray-300 bg-transparent px-3.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
            [class.border-error-300]="duplicateError"
          />
          <button
            type="button"
            (click)="addOption()"
            [disabled]="!newOptionInput.trim()"
            class="h-10 px-4 text-sm font-medium rounded-lg bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0">
            Agregar
          </button>
        </div>

        @if (duplicateError) {
        <p class="mt-1.5 text-xs text-error-500">Esa opción ya existe en la lista</p>
        } @else {
        <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">Puedes reordenar las opciones con las flechas ↑ ↓</p>
        }
      </div>
      }

      <!-- ── Botones ── -->
      <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <app-button
          variant="outline"
          type="button"
          (btnClick)="cancelar()"
        >
          Cancelar
        </app-button>
        <app-button
          variant="primary"
          type="submit"
          [disabled]="saving"
        >
          @if (saving) {
            <svg class="animate-spin w-4 h-4 mr-1.5 inline" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            Guardando...
          } @else {
            {{ isEditing ? 'Actualizar definición' : 'Crear definición' }}
          }
        </app-button>
      </div>
    </form>
    }
  </app-component-card>
</div>
